import React, { useId } from 'react';
import PropTypes from 'prop-types';

/**
 * FormField wrapper component for consistent form styling
 * Provides label, error message, and helper text support
 */
export function FormField({
  label,
  error,
  helperText,
  required = false,
  children,
  className = '',
  htmlFor,
  ...props
}) {
  // Generate a unique ID using React's useId hook for robust ID generation
  const generatedId = useId();
  const fieldId = htmlFor || (label ? generatedId : undefined);

  return (
    <div className={`${className}`} {...props}>
      {/* Label */}
      {label && (
        <label
          htmlFor={fieldId}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
          {required && (
            <span className="text-red-500 ml-1" aria-label="required">
              *
            </span>
          )}
        </label>
      )}

      {/* Input element (children) */}
      <div className="relative">
        {React.Children.map(children, child => {
          // Clone the child element and add the id if it's an input-like element
          if (React.isValidElement(child) && fieldId) {
            return React.cloneElement(child, {
              id: fieldId,
              'aria-invalid': !!error,
              'aria-describedby': error ? `${fieldId}-error` : helperText ? `${fieldId}-helper` : undefined,
              ...child.props
            });
          }
          return child;
        })}
      </div>

      {/* Error message */}
      {error && (
        <p
          id={`${fieldId}-error`}
          className="mt-1 text-sm text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}

      {/* Helper text */}
      {helperText && !error && (
        <p
          id={`${fieldId}-helper`}
          className="mt-1 text-sm text-gray-500"
        >
          {helperText}
        </p>
      )}
    </div>
  );
}

// PropTypes validation
FormField.propTypes = {
  label: PropTypes.string,
  error: PropTypes.string,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  htmlFor: PropTypes.string
};